import React, { useState, useEffect, useCallback, useMemo, Suspense, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { includes, isEmpty, isEqual, map } from 'lodash';
import { IconShowFiltersButton } from 'svgIcons/MotionPortalIcons';
import { allAssetTypes, apiCallStatus, newConditionIndices, MAX_INPUT_CHAR_LENGTH } from 'helpers/constants';
import { LoadingStatusWrapper } from 'sharedComponents/WithLoadingStatus';
import { ToggleButton } from 'sharedComponents/ToggleButton';
import { useQueryParams } from 'sharedHooks/useQuery';
import { LoadingIndicator } from 'sharedComponents/PowertrainLoadingIndicator';
import { SearchBox, ThreeColumnLayout } from 'sharedComponents';
import { Filter } from './FilterComponent';
import { tabIDs } from '../../PowertrainAssetDetails/DetailedInformation/constants';
import { setActiveTab as setConditionInsightsActiveTab, setDriveAnomalyActiveTab } from '../../PowertrainAssetDetails/DetailedInformation/ConditionInsights/actions';
import { conditionInsightsTabs } from '../../PowertrainAssetDetails/DetailedInformation/ConditionInsights/helpers';
import { driveAnomalyTabs, EMAIL_SUB_TAB_ANOMALY } from '../../PowertrainAssetDetails/DetailedInformation/ConditionInsights/constants';
// import { AssetOverview } from './AssetOverview';
import { AssetGroupOverview } from './AssetGroupOverview';
import { PowertrainOverview } from './PowertrainOverview';
import {
    getToggleButtonOptions,
    getFilterVisibilityFromLocalStorage,
    setFilterVisibilityToLocalStorage,
    getOverviewNumber,
    isTabContentLoading,
    iconCondition,
    getTabSpecificFilter,
    handleRemoveQueryParams,
    countHierarchyAssets,
    collectHierarchyAssets
} from './helpers';
import { toggleButtonOptions, conditionIndices, LEFT_PANEL_WIDTH_PERCENT, conditionIndicesViewOptions, WALKABLE_KEYS } from './constants';
import './style.scss';
import useUserCapability from 'sharedHooks/useUserCapability';
import { AssetOverviewDetails } from '../../PowertrainConditionMonitoring/AssetOverviewDeatilsV2';
import HierarchyViewContainer from './HierarchyView/HierarchyViewContainer';

const getSearchableAssetSerial = (asset) => {
    return (asset?.assetSerialNumber || asset?.serialNumber || '').toString().toLowerCase();
};

const isAssetMatchingSearch = (asset, normalizedSearch) => {
    if (!asset || !normalizedSearch) {
        return false;
    }

    const assetName = (asset?.assetName || asset?.name || '').toString().toLowerCase();
    const assetId = (asset?.assetId || asset?.id || '').toString().toLowerCase();
    const assetSerial = getSearchableAssetSerial(asset);

    return assetName.includes(normalizedSearch)
        || assetId.includes(normalizedSearch)
        || assetSerial.includes(normalizedSearch);
};

const walkAssets = (node, collector) => {
    if (!node) {
        return;
    }

    if (Array.isArray(node)) {
        node.forEach((item) => {
            walkAssets(item, collector);
        });
        return;
    }

    if (typeof node !== 'object') {
        return;
    }

    if (node?.assetId || node?.id) {
        collector.push(node);
    }

    WALKABLE_KEYS.forEach((key) => {
        walkAssets(node[key], collector);
    });
};

const findAssetInLocation = (location, matcher) => {
    const directAsset = location?.assets?.find(matcher);
    if (directAsset) { return directAsset; }
    for (const powertrain of location?.powertrains || []) {
        const found = powertrain?.assets?.find(matcher);
        if (found) { return found; }
    }
    for (const subLocation of location?.subLocations || []) {
        const found = findAssetInLocation(subLocation, matcher);
        if (found) { return found; }
    }
    return null;
};

const findAssetInHierarchyData = (hierarchyData, matcher) => {
    for (const site of hierarchyData?.sites || []) {
        for (const location of site?.locations || []) {
            const found = findAssetInLocation(location, matcher);
            if (found) { return found; }
        }
        const notConfigured = site?.notConfiguredAssets?.find(matcher);
        if (notConfigured) { return notConfigured; }
        for (const powertrain of site?.notConfiguredPowertrains || []) {
            const found = powertrain?.assets?.find(matcher);
            if (found) { return found; }
        }
    }
    return null;
};

const findAssetInGroups = (groups, normalizedId) => {
    const matchInGroup = (ast) => ast?.assetId?.toString() === normalizedId || ast?.assetSerialNumber?.toLowerCase() === normalizedId;
    const group = groups?.find((g) => g?.assets?.find(matchInGroup));
    return group?.assets?.find((ast) => ast?.assetId?.toString() === normalizedId) || null;
};

const findFirstMatchingAssetFromData = (data, searchTerm) => {
    const normalizedSearch = searchTerm?.trim()?.toLowerCase();
    if (!normalizedSearch) {
        return null;
    }

    const assets = [];
    walkAssets(data, assets);

    return assets.find((asset) => isAssetMatchingSearch(asset, normalizedSearch)) || null;
};

// eslint-disable-next-line react/display-name
const ConditionIndicesOverviewComponent = React.memo(({ model, actions }) => {
    const { t: translate } = useTranslation();
    const { assetId, tabId, subTab, subTab2 } = useQueryParams();
    const history = useHistory();
    const dispatch = useDispatch();
    const userCapabilities = useUserCapability();
    const hasConditionOverviewSubscriptionFilter = userCapabilities?.hasConditionOverviewSubscriptionFilter();
    const [showFilter, setShowFilter] = useState(getFilterVisibilityFromLocalStorage());
    const [leftPanelSize, setLeftPanelSize] = useState(100);
    const lastHandledSearchKeyRef = useRef(null);
    const lastAutoSelectedSearchRef = useRef(null);

    const {
        selectedFilters,
        assetsSearchBoxFilter,
        assetGroupsSearchBoxFilter,
        powertrainsSearchBoxFilter,
        assetsAssetTypeFilter,
        assetGroupsAssetTypeFilter,
        powertrainsAssetTypeFilter,
        assetsConditionFilter,
        assetGroupsConditionFilter,
        powertrainsConditionFilter,
        assetsSubscriptionTypeFilter,
        assetGroupsSubscriptionTypeFilter,
        powertrainsSubscriptionTypeFilter,
        assetsSensorIdentifierFilter,
        assetGroupsSensorIdentifierFilter,
        powertrainsSensorIdentifierFilter,
        assetNumber,
        assetGroupNumber,
        assetGroupAssetNumber,
        powertrainNumber,
        powertrainAssetNumber,
        activeTab,
        generalFilterLoadingStatuses,
        assetOverviewLoadingStatus,
        assetGroupOverviewLoadingStatus,
        powertrainOverviewLoadingStatus,
        allAssets,
        subscriptionTypes,
        selectedOrganization,
        isFilterInProgress,
        prevSelectedFilters,
        selectedAsset,
        selectedNotification,
        conditionIndicesV2AssetList,
        conditionIndicesV2PowerTrain,
        conditionIndicesV2AssetGroups,
        sites,
        selectedAssetDetails,
        allAssetsLoadingStatus,
        isNavigationSearch,
        conditionIndicesView,
        hierarchyConditionIndicesLoadingStatus,
        hierarchySearchBoxFilter,
        hierarchySensorIdentifierFilter,
        hierarchySubscriptionTypeFilter,
        hierarchyConditionFilter,
        hierarchyAssetTypeFilter,
        hierarchyLastUpdatedTimestamp,
        hierarchyConditionIndicesData,
        assetsLastUpdatedTimestamp,
        listConditionIndicesData
    } = model;

    const {
        setAssetTypeFilter,
        toggleConditionFilter,
        resetFilters,
        setSearchboxFilter,
        setSubscriptionTypeFilter,
        setActiveTab,
        setSensorIdentifierFilter,
        getSubscriptionTypes,
        setConditionIndicesView,
        getHierarchyConditionIndices,
        setActiveTabV2
    } = actions;

    const handleSetConditionIndicesView = useCallback((view) => {
        setConditionIndicesView(view);
    }, [setConditionIndicesView]);
    const organizationId = selectedOrganization?.organizationID;
    const windowWidth = window.innerWidth;

    const selectedTabConditionFilter = getTabSpecificFilter(activeTab, assetsConditionFilter, assetGroupsConditionFilter, powertrainsConditionFilter, '', conditionIndicesView, hierarchyConditionFilter);
    const selectedTabSubscriptionTypeFilter = getTabSpecificFilter(activeTab, assetsSubscriptionTypeFilter, assetGroupsSubscriptionTypeFilter, powertrainsSubscriptionTypeFilter, '', conditionIndicesView, hierarchySubscriptionTypeFilter);
    const selectedTabAssetTypeFilter = getTabSpecificFilter(activeTab, assetsAssetTypeFilter, assetGroupsAssetTypeFilter, powertrainsAssetTypeFilter, '', conditionIndicesView, hierarchyAssetTypeFilter);
    const selectedTabSearchBoxFilter = getTabSpecificFilter(activeTab, assetsSearchBoxFilter, assetGroupsSearchBoxFilter, powertrainsSearchBoxFilter, '', conditionIndicesView, hierarchySearchBoxFilter);
    const selectedTabSensorIdentifierFilter = getTabSpecificFilter(activeTab, assetsSensorIdentifierFilter, assetGroupsSensorIdentifierFilter, powertrainsSensorIdentifierFilter, '', conditionIndicesView, hierarchySensorIdentifierFilter);
    const setNavigationSearch = actions?.setNavigationSearch;

    /* REMOVE QUERY PARAMS ON ANY CLIK ON THE PAGE TO AVOID BACK FIRE/NAVIGATION */
    // const onDocumentClick = () => {
    //     handleRemoveQueryParams(history);
    // };
    // useEffect(() => {
    //     document.addEventListener('click', onDocumentClick);

    //     return () => {
    //         document.removeEventListener('click', onDocumentClick);
    //     };
    // }, []);

    const requestHierarchyData = () => {
        if (organizationId) {
            const sensorIdentifiers = selectedTabSensorIdentifierFilter
                ? selectedTabSensorIdentifierFilter.split(',').map(item => item.trim()).filter(item => item.length > 0)
                : [];
            const siteIds = selectedFilters?.sitesChecked
                ? (selectedFilters.sites || []).map(site => site.siteID)
                : [];
            const payload = {
                organizationId: organizationId,
                siteIds: siteIds,
                isIncludeLocationsOnly: conditionIndicesView === conditionIndicesViewOptions.HIERARCHY,
                isFlat: conditionIndicesView === conditionIndicesViewOptions.LIST,
                assetGroupId: null,
                conditionFilter: selectedTabConditionFilter,
                assetTypeFilter: selectedTabAssetTypeFilter === allAssetTypes ? '' : selectedTabAssetTypeFilter,
                subscriptionTypeFilter: selectedTabSubscriptionTypeFilter,
                sensorIdentifiers,
                sortingOrder: 'asc',
                refreshData: true
            };
            if (selectedTabSearchBoxFilter) {
                const trimmedFilter = selectedTabSearchBoxFilter.trim();
                if (trimmedFilter.length > 0) {
                    payload.filter = trimmedFilter;
                }
            }
            getHierarchyConditionIndices(payload, conditionIndicesView);
        }
    };

    const filterVisibilityChange = useCallback(
        (visibility) => {
            setShowFilter(visibility);
            setFilterVisibilityToLocalStorage(visibility);
        },
        []
    );

    // Memoize tab content loading status
    const tabContentLoading = useMemo(
        () =>
            isTabContentLoading(
                activeTab,
                assetOverviewLoadingStatus,
                assetGroupOverviewLoadingStatus,
                powertrainOverviewLoadingStatus,
                hierarchyConditionIndicesLoadingStatus
            ),
        [activeTab, assetOverviewLoadingStatus, assetGroupOverviewLoadingStatus, powertrainOverviewLoadingStatus, hierarchyConditionIndicesLoadingStatus]
    );

    const hierarchyAssetCount = useMemo(
        () => countHierarchyAssets(hierarchyConditionIndicesData),
        [hierarchyConditionIndicesData]
    );

    const listAssetCount = useMemo(
        () => countHierarchyAssets(listConditionIndicesData),
        [listConditionIndicesData]
    );

    const renderConditionIndexButtons = useCallback((keys) => {
        return map(keys, (key) => {
            const conditionIndex = newConditionIndices[key];
            const isDisabled = tabContentLoading;
            return (
                <div className='item' key={key}>
                    <button
                        className={`condition-index${includes(selectedTabConditionFilter, conditionIndex.conditionIndexID) ? ' selected' : ''}${isDisabled ? ' disabled' : ''}`}
                        onClick={() => { if (!isDisabled) { toggleConditionFilter(conditionIndex.conditionIndexID); } }}
                    >
                        <span className={`asset-condition ${iconCondition(conditionIndex?.conditionIndexID)}`}></span>
                        <span>{translate(`ABB.Powertrain.Frontend.${conditionIndex.translationKey}Label`)}</span>
                    </button>
                </div>
            );
        });
    }, [tabContentLoading, selectedTabConditionFilter, toggleConditionFilter, translate]);

    useEffect(() => {
        if (hasConditionOverviewSubscriptionFilter && organizationId) {
            getSubscriptionTypes(organizationId);
        }
    }, [hasConditionOverviewSubscriptionFilter, organizationId]);

    useEffect(() => {
        if (assetId) {
            setNavigationSearch(true);
            setActiveTab(toggleButtonOptions.ASSETS);
            setSearchboxFilter(assetId);
        }
    }, [assetId]);

    useEffect(() => {
        if (prevSelectedFilters &&
            !isEqual(prevSelectedFilters, selectedFilters)) {
            resetFilters();
        }
    }, [selectedFilters, prevSelectedFilters]);

    const handleSearchChange = useCallback((value) => {
        setNavigationSearch(false);
        setSearchboxFilter(value);
    }, []);

    // const handleTriggerTabToggle = () => {
    //     if (searchBoxFilter !== null) {
    //         setSearchboxFilter(null);
    //     }
    // };

    const handleNotification = (id) => {
        if (!id || id === '') { return; }

        const normalizedId = id?.toString()?.toLowerCase();
        actions.setSelectedAssetAction(null, allAssets);

        let asset = null;
        if (activeTab === toggleButtonOptions.ASSETGROUPS) {
            asset = findAssetInGroups(conditionIndicesV2AssetGroups, normalizedId);
        } else if (activeTab === toggleButtonOptions.POWERTRAINS) {
            asset = findAssetInGroups(conditionIndicesV2PowerTrain, normalizedId);
        } else {
            const hierarchyData = conditionIndicesView === conditionIndicesViewOptions.HIERARCHY ? hierarchyConditionIndicesData : conditionIndicesV2AssetList;
            const matcher = (ast) => ast?.assetId?.toString() === normalizedId || ast?.serialNumber?.toLowerCase() === normalizedId;
            asset = findAssetInHierarchyData(hierarchyData, matcher);
        }

        if (asset) {
            actions.setSelectedAssetAction(asset, allAssets);
        }
        setActiveTab(toggleButtonOptions.ASSETS);
    };

    const applyConditionInsightsEmailSubTab = (subTabParam, subTab2Param) => {
        if (subTabParam?.toLowerCase() !== EMAIL_SUB_TAB_ANOMALY) {
            dispatch(setConditionInsightsActiveTab(conditionInsightsTabs.CONDITION_INDICES));
            return;
        }
        dispatch(setConditionInsightsActiveTab(conditionInsightsTabs.ANOMALY_DETECTION));
        const normalizedSubTab2 = subTab2Param?.toLowerCase();
        if (normalizedSubTab2 === driveAnomalyTabs.ENERGY) {
            dispatch(setDriveAnomalyActiveTab(driveAnomalyTabs.ENERGY));
        } else if (normalizedSubTab2 === driveAnomalyTabs.COOLING) {
            dispatch(setDriveAnomalyActiveTab(driveAnomalyTabs.COOLING));
        }
    };

    /* HANDLE NOTIFICATION FROM PANEL AND SET ASSET */
    useEffect(() => {
        const isCrossOrgNotification = organizationId && selectedNotification?.organizationId &&
            String(organizationId) !== String(selectedNotification.organizationId);
        if (selectedNotification && !isEmpty(selectedNotification) && !isCrossOrgNotification) {
            handleNotification(selectedNotification?.assetId);
        }
    }, [selectedNotification, organizationId, allAssetsLoadingStatus, assetOverviewLoadingStatus, assetGroupOverviewLoadingStatus, powertrainOverviewLoadingStatus, hierarchyConditionIndicesLoadingStatus]);

    const canHandleEmailAssetNavigation = (hierarchyData) =>
        !!(assetId && assetId !== '' && allAssetsLoadingStatus === apiCallStatus.SUCCESS && hierarchyData?.organizationId);

    const canHandleNavigationSearch = (hierarchyData) =>
        !!(isNavigationSearch && selectedTabSearchBoxFilter !== '' && allAssetsLoadingStatus === apiCallStatus.SUCCESS && hierarchyData?.organizationId);

    const applyEmailAssetNavigation = () => {
        handleNotification(assetId);
        lastHandledSearchKeyRef.current = `asset:${assetId}`;
        if (tabId) {
            setActiveTabV2(tabId);
        }
        if (tabId === tabIDs.conditionInsights && subTab && subTab !== '') {
            applyConditionInsightsEmailSubTab(subTab, subTab2);
        }
        handleRemoveQueryParams(history);
    };

    const applyNavigationSearchFilter = () => {
        handleNotification(selectedTabSearchBoxFilter);
        lastHandledSearchKeyRef.current = `filter:${selectedTabSearchBoxFilter}`;
    };

    /* HANDLE NOTIFICATION FROM EMAIL / FLEET NAVIGATION  AND SET ASSET */
    useEffect(() => {
        const hierarchyData = conditionIndicesView === conditionIndicesViewOptions.HIERARCHY ? hierarchyConditionIndicesData : conditionIndicesV2AssetList;

        if (canHandleEmailAssetNavigation(hierarchyData)) {
            if (lastHandledSearchKeyRef.current !== `asset:${assetId}`) {
                applyEmailAssetNavigation();
            }
            return;
        }

        if (canHandleNavigationSearch(hierarchyData)) {
            if (lastHandledSearchKeyRef.current !== `filter:${selectedTabSearchBoxFilter}`) {
                applyNavigationSearchFilter();
            }
            return;
        }

        if (!selectedAssetDetails && !selectedNotification && selectedAsset) {
            actions.setSelectedAssetAction(null);
            lastHandledSearchKeyRef.current = null;
        }
    }, [assetId, tabId, subTab, subTab2, selectedTabSearchBoxFilter, isNavigationSearch, allAssetsLoadingStatus, selectedAssetDetails, selectedNotification, conditionIndicesV2AssetList, hierarchyConditionIndicesData]);

    useEffect(() => {
        // HierarchyViewContainer renders for the HIERARCHY view regardless of the tab toggle
        // (which is hidden there), and for the LIST view only on the ASSETS tab.
        const isHierarchyView = conditionIndicesView === conditionIndicesViewOptions.HIERARCHY;
        const isListAssetsView = conditionIndicesView === conditionIndicesViewOptions.LIST && activeTab === toggleButtonOptions.ASSETS;

        if (!isHierarchyView && !isListAssetsView) {
            return;
        }

        const searchTerm = selectedTabSearchBoxFilter?.trim();
        const sensorIdentifierTerm = selectedTabSensorIdentifierFilter?.trim();

        if (!searchTerm && !sensorIdentifierTerm) {
            lastAutoSelectedSearchRef.current = null;
            return;
        }

        const viewLoadingStatus = conditionIndicesView === conditionIndicesViewOptions.HIERARCHY
            ? hierarchyConditionIndicesLoadingStatus
            : assetOverviewLoadingStatus;

        if (viewLoadingStatus !== apiCallStatus.SUCCESS) {
            return;
        }

        const sourceData = conditionIndicesView === conditionIndicesViewOptions.HIERARCHY
            ? hierarchyConditionIndicesData
            : conditionIndicesV2AssetList;

        if (searchTerm) {
            const autoSelectKey = `search:${searchTerm}`;
            if (lastAutoSelectedSearchRef.current === autoSelectKey) {
                return;
            }

            const matchingAsset = findFirstMatchingAssetFromData(sourceData, searchTerm);
            if (matchingAsset) {
                lastAutoSelectedSearchRef.current = autoSelectKey;
                actions.setSelectedAssetAction(matchingAsset, allAssets);
            }
            return;
        }


        const autoSelectKey = `sensor:${sensorIdentifierTerm}`;
        if (lastAutoSelectedSearchRef.current === autoSelectKey) {
            return;
        }

        const matchedAssets = collectHierarchyAssets(sourceData);
        if (matchedAssets.length === 1) {
            lastAutoSelectedSearchRef.current = autoSelectKey;
            actions.setSelectedAssetAction(matchedAssets[0], allAssets);
        }
    }, [
        activeTab,
        conditionIndicesView,
        selectedTabSearchBoxFilter,
        selectedTabSensorIdentifierFilter,
        hierarchyConditionIndicesLoadingStatus,
        assetOverviewLoadingStatus,
        hierarchyConditionIndicesData,
        conditionIndicesV2AssetList,
        allAssets
    ]);

    // Memoize the selected tab rendering logic
    const renderSelectedTab = useMemo(() => {
        switch (activeTab) {
            case toggleButtonOptions.ASSETGROUPS:
                return (
                    <LoadingStatusWrapper loadingStatus={[assetGroupOverviewLoadingStatus, allAssetsLoadingStatus]}>
                        <Suspense fallback={<LoadingIndicator />}>
                            <AssetGroupOverview
                                sites={sites}
                                selectedFilters={selectedFilters}
                                searchBoxFilter={assetGroupsSearchBoxFilter}
                                sensorIdentifierFilter={assetGroupsSensorIdentifierFilter}
                                conditionFilter={assetGroupsConditionFilter}
                                assetTypeFilter={assetGroupsAssetTypeFilter}
                                subscriptionTypeFilter={assetGroupsSubscriptionTypeFilter}
                                generalFilterLoadingStatuses={generalFilterLoadingStatuses}
                                withVisibleFilter={showFilter}
                                allAssets={allAssets}
                                isFilterInProgress={isFilterInProgress}
                                leftPanelSize={leftPanelSize}
                            />
                        </Suspense>
                    </LoadingStatusWrapper >
                );
            // case toggleButtonOptions.POWERTRAINS:
            default:
                return (
                    <LoadingStatusWrapper loadingStatus={[powertrainOverviewLoadingStatus, allAssetsLoadingStatus]}>
                        <Suspense fallback={<LoadingIndicator />}>
                            <PowertrainOverview
                                sites={sites}
                                selectedFilters={selectedFilters}
                                searchBoxFilter={powertrainsSearchBoxFilter}
                                sensorIdentifierFilter={powertrainsSensorIdentifierFilter}
                                conditionFilter={powertrainsConditionFilter}
                                assetTypeFilter={powertrainsAssetTypeFilter}
                                subscriptionTypeFilter={powertrainsSubscriptionTypeFilter}
                                generalFilterLoadingStatuses={generalFilterLoadingStatuses}
                                withVisibleFilter={showFilter}
                                allAssets={allAssets}
                                isFilterInProgress={isFilterInProgress}
                                leftPanelSize={leftPanelSize}
                            />
                        </Suspense>
                    </LoadingStatusWrapper >
                );
            // default:
            //     return (
            //         <LoadingStatusWrapper transparent={conditionIndicesV2AssetList?.length > 0} loadingStatus={[assetOverviewLoadingStatus, allAssetsLoadingStatus]}>
            //             <Suspense fallback={<LoadingIndicator />}>
            //                 <AssetOverview
            //                     sites={sites}
            //                     selectedFilters={selectedFilters}
            //                     searchBoxFilter={assetsSearchBoxFilter}
            //                     sensorIdentifierFilter={assetsSensorIdentifierFilter}
            //                     conditionFilter={assetsConditionFilter}
            //                     assetTypeFilter={assetsAssetTypeFilter}
            //                     subscriptionTypeFilter={assetsSubscriptionTypeFilter}
            //                     generalFilterLoadingStatuses={generalFilterLoadingStatuses}
            //                     withVisibleFilter={showFilter}
            //                     allAssets={allAssets}
            //                     isFilterInProgress={isFilterInProgress}
            //                     leftPanelSize={leftPanelSize}
            //                 />
            //             </Suspense>
            //         </LoadingStatusWrapper>
            //     );
        }
    }, [
        activeTab,
        selectedFilters,
        assetsSearchBoxFilter,
        assetGroupsSearchBoxFilter,
        powertrainsSearchBoxFilter,
        assetsConditionFilter,
        assetGroupsConditionFilter,
        powertrainsConditionFilter,
        assetsAssetTypeFilter,
        assetGroupsAssetTypeFilter,
        powertrainsAssetTypeFilter,
        assetsSubscriptionTypeFilter,
        assetGroupsSubscriptionTypeFilter,
        powertrainsSubscriptionTypeFilter,
        assetsSensorIdentifierFilter,
        assetGroupsSensorIdentifierFilter,
        powertrainsSensorIdentifierFilter,
        generalFilterLoadingStatuses,
        showFilter,
        allAssets,
        leftPanelSize,
        conditionIndicesV2AssetList
    ]);

    return <ThreeColumnLayout
        mainLayoutclass='condition-overview-layout-v2'
        leftPanelDefaulWidth={100}
        leftPanelMinSize={windowWidth > 1400 ? 25 : 35}
        leftPanelCollapsedSize={windowWidth > 1400 ? 3 : 4}
        centerPanelDefaultSize={30}
        centerPanelMinSize={50}
        leftPanelHeader={translate('ABB.Powertrain.Frontend.conditionIndicesOverviewOverviewTitle')}
        onResizeLeftPanel={(size) => { setLeftPanelSize(size); }}
        isForConditionOverview={true}
        conditionIndicesView={conditionIndicesView}
        setConditionIndicesView={handleSetConditionIndicesView}
        requestHierarchyData={requestHierarchyData}
        lastUpdatedTimestamp={hierarchyLastUpdatedTimestamp}
        assetsLastUpdatedTimestamp={assetsLastUpdatedTimestamp}
        activeTab={activeTab}
    >
        {/* Left Panel Content */}
        <div className={`powertrain-assets-overview-container-v2 ${selectedAsset && selectedAssetDetails && leftPanelSize > LEFT_PANEL_WIDTH_PERCENT ? 'asset-selected' : ''} ${conditionIndicesView === conditionIndicesViewOptions.HIERARCHY ? 'hierarchy-container-view' : ''}`}>
            {/* <div className='overview-title'>
                    {`${translate('ABB.Powertrain.Frontend.conditionIndicesOverviewOverviewTitle')}`}
                </div> */}
            {conditionIndicesView === conditionIndicesViewOptions.HIERARCHY ?
                // Hierarchy view: single aligned row
                <>
                    <div className={`hierarchy-status-row ${tabContentLoading ? 'show-overlay' : ''}`}>
                        {/* <div className='overlay'></div> */}
                        <div className='condition-index-filter'>
                            <div className='section-title'>
                                {translate('ABB.Powertrain.Frontend.overallAssetStatus')}
                            </div>
                            <div className='condition-indexes'>
                                {renderConditionIndexButtons(['Poor', 'Tolerable', 'Unknown', 'Good'])}
                            </div>
                        </div>
                        <div className='hierarchy-right-section'>
                            <div className='nr-of-assets'>
                                {`${hierarchyAssetCount} ${translate('ABB.Powertrain.Frontend.conditionIndicesOverviewAssetNumber')}`}
                            </div>
                            <div className='search-filter-group'>
                                <SearchBox
                                    placeholder={translate('ABB.Powertrain.Frontend.nameOrSerialNoLabel')}
                                    onChange={handleSearchChange}
                                    value={selectedTabSearchBoxFilter}
                                    maxLength={MAX_INPUT_CHAR_LENGTH}
                                />
                                <button className='filter-icon' onClick={() => filterVisibilityChange(!showFilter)}>
                                    <IconShowFiltersButton />
                                </button>
                            </div>
                        </div>
                    </div>
                    {showFilter ? <Suspense fallback={<div>Loading...</div>}><Filter
                        setAssetTypeFilter={setAssetTypeFilter}
                        assetTypeFilter={selectedTabAssetTypeFilter}
                        conditionFilter={selectedTabConditionFilter}
                        toggleConditionFilter={toggleConditionFilter}
                        subscriptionTypes={subscriptionTypes}
                        setSubscriptionTypeFilter={setSubscriptionTypeFilter}
                        subscriptionTypeFilter={selectedTabSubscriptionTypeFilter}
                        setSensorIdentifierFilter={setSensorIdentifierFilter}
                        sensorIdentifierFilter={selectedTabSensorIdentifierFilter}
                        resetFilters={() => {
                            history.replace(history.location.pathname);
                            resetFilters();
                        }}
                        isDisabled={tabContentLoading}
                        isResetFilterDisabled={
                            tabContentLoading ||
                            selectedTabAssetTypeFilter === 'AllAssetTypes' &&
                            (!selectedTabSearchBoxFilter || selectedTabSearchBoxFilter.length === 0) &&
                            (!selectedTabSensorIdentifierFilter || selectedTabSensorIdentifierFilter.length === 0) &&
                            (!selectedTabConditionFilter || selectedTabConditionFilter.length === 0) &&
                            (!selectedTabSubscriptionTypeFilter || selectedTabSubscriptionTypeFilter.length === 0)
                        }
                        assetTypeFilterVisible={true}
                        allAssets={allAssets}
                        leftPanelSize={leftPanelSize}
                    /></Suspense> : <br />}
                </>
                :
                // Non-hierarchy tab existing view
                <>
                    <div className={'toggle-and-search-row'}>
                        <ToggleButton
                            options={getToggleButtonOptions(translate)}
                            onSelected={(tab) => {
                                setActiveTab(tab);
                                handleRemoveQueryParams(history);
                                actions.setSelectedNotification(null);
                            }}
                            selectedValue={activeTab}
                        // triggerToggle={handleTriggerTabToggle}
                        />
                        <div className='right-section'>
                            <div className='nr-of-assets'>
                                {activeTab === toggleButtonOptions.ASSETS
                                    ? `${listAssetCount} ${translate('ABB.Powertrain.Frontend.conditionIndicesOverviewAssetNumber')}`
                                    : getOverviewNumber(activeTab, assetNumber, assetGroupNumber, assetGroupAssetNumber, powertrainNumber, powertrainAssetNumber, translate)
                                }
                            </div>
                            <SearchBox
                                placeholder={translate('ABB.Powertrain.Frontend.nameOrSerialNoLabel')}
                                onChange={handleSearchChange}
                                value={selectedTabSearchBoxFilter}
                                maxLength={MAX_INPUT_CHAR_LENGTH}
                            />
                        </div>
                    </div>

                    <div className={tabContentLoading ? 'show-overlay' : ''}>
                        <div className='overlay'></div>
                        <div className='asset-status-filtering'>
                            <div className='condition-index-filter'>
                                <div className='section-title'>
                                    {translate('ABB.Powertrain.Frontend.overallAssetStatus')}
                                </div>
                                <div className='condition-indexes'>
                                    {activeTab === toggleButtonOptions.ASSETS
                                        ? renderConditionIndexButtons(['Poor', 'Tolerable', 'Unknown', 'Good'])
                                        : renderConditionIndexButtons(
                                            Object.keys(newConditionIndices).filter(k => newConditionIndices[k].conditionIndexID !== conditionIndices.UNSUPPORTED)
                                        )
                                    }
                                </div>
                            </div>
                            <div className='filter-btn'>
                                <button className='filter-icon' onClick={() => filterVisibilityChange(!showFilter)}>
                                    <IconShowFiltersButton />
                                </button>
                            </div>
                        </div>
                        {showFilter ? <Suspense fallback={<div>Loading...</div>}><Filter
                            setAssetTypeFilter={setAssetTypeFilter}
                            assetTypeFilter={selectedTabAssetTypeFilter}
                            conditionFilter={selectedTabConditionFilter}
                            toggleConditionFilter={toggleConditionFilter}
                            subscriptionTypes={subscriptionTypes}
                            setSubscriptionTypeFilter={setSubscriptionTypeFilter}
                            subscriptionTypeFilter={selectedTabSubscriptionTypeFilter}
                            setSensorIdentifierFilter={setSensorIdentifierFilter}
                            sensorIdentifierFilter={selectedTabSensorIdentifierFilter}
                            resetFilters={() => {
                                history.replace(history.location.pathname);
                                resetFilters();
                            }}
                            isDisabled={tabContentLoading}
                            isResetFilterDisabled={
                                tabContentLoading ||
                                selectedTabAssetTypeFilter === 'AllAssetTypes' &&
                                (!selectedTabSearchBoxFilter || selectedTabSearchBoxFilter.length === 0) &&
                                (!selectedTabSensorIdentifierFilter || selectedTabSensorIdentifierFilter.length === 0) &&
                                (!selectedTabConditionFilter || selectedTabConditionFilter.length === 0) &&
                                (!selectedTabSubscriptionTypeFilter || selectedTabSubscriptionTypeFilter.length === 0)
                            }
                            assetTypeFilterVisible={activeTab === toggleButtonOptions.ASSETS}
                            allAssets={allAssets}
                            leftPanelSize={leftPanelSize}
                        /></Suspense> : <br />}
                    </div>
                </>
            }
            {conditionIndicesView === conditionIndicesViewOptions.HIERARCHY ||
                conditionIndicesView === conditionIndicesViewOptions.LIST && activeTab === toggleButtonOptions.ASSETS ?
                <Suspense fallback={<div>Loading...</div>}>
                    <HierarchyViewContainer
                        selectedAsset={selectedAsset}
                        leftPanelSize={leftPanelSize}
                        allAssets={allAssets}
                        conditionIndicesView={conditionIndicesView}
                        showFilter={showFilter}
                        onSelectAsset={(asset) => {
                            setNavigationSearch(false);
                            actions.setSelectedAssetAction(asset, allAssets);
                        }}
                        generalFilterLoadingStatuses={generalFilterLoadingStatuses}
                        activeTab={activeTab}
                        selectedOrganization={selectedOrganization}
                    />
                </Suspense> :
                <div className='selected-tab-content'>
                    <Suspense fallback={<div>Loading...</div>}>
                        {renderSelectedTab}
                    </Suspense>
                </div>
            }
        </div>
        {/* Right Panel Content */}
        {selectedAsset && selectedAssetDetails &&
            <div className={`asset-overview-details-v2 ${leftPanelSize < 25 ? 'collapsed' : ''}`}>
                <AssetOverviewDetails selectedAsset={selectedAsset} />
            </div>
        }
    </ThreeColumnLayout>;
});

ConditionIndicesOverviewComponent.propTypes = {
    model: PropTypes.shape({
        selectedFilters: PropTypes.object,
        assetsSearchBoxFilter: PropTypes.string,
        assetGroupsSearchBoxFilter: PropTypes.string,
        powertrainsSearchBoxFilter: PropTypes.string,
        assetsConditionFilter: PropTypes.array,
        powertrainsConditionFilter: PropTypes.array,
        assetGroupsConditionFilter: PropTypes.array,
        assetsAssetTypeFilter: PropTypes.string,
        assetGroupsAssetTypeFilter: PropTypes.string,
        powertrainsAssetTypeFilter: PropTypes.string,
        assetsSubscriptionTypeFilter: PropTypes.array,
        assetGroupsSubscriptionTypeFilter: PropTypes.array,
        powertrainsSubscriptionTypeFilter: PropTypes.array,
        assetsSensorIdentifierFilter: PropTypes.string,
        assetGroupsSensorIdentifierFilter: PropTypes.string,
        powertrainsSensorIdentifierFilter: PropTypes.string,
        assetNumber: PropTypes.number,
        assetGroupNumber: PropTypes.number,
        assetGroupAssetNumber: PropTypes.number,
        powertrainNumber: PropTypes.number,
        powertrainAssetNumber: PropTypes.number,
        activeTab: PropTypes.string,
        generalFilterLoadingStatuses: PropTypes.array,
        assetOverviewLoadingStatus: PropTypes.string,
        assetGroupOverviewLoadingStatus: PropTypes.string,
        powertrainOverviewLoadingStatus: PropTypes.string,
        allAssetsLoadingStatus: PropTypes.string,
        isFilterInProgress: PropTypes.bool,
        prevSelectedFilters: PropTypes.object,
        subscriptionTypeFilter: PropTypes.array,
        allAssets: PropTypes.array,
        subscriptionTypes: PropTypes.array,
        selectedOrganization: PropTypes.shape({
            organizationID: PropTypes.number
        }),
        isNavigationSearch: PropTypes.bool,
        selectedAsset: PropTypes.shape({
            assetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            assetName: PropTypes.string,
            assetSerialNumber: PropTypes.string,
        }),
        selectedNotification: PropTypes.shape({
            assetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            organizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
        conditionIndicesV2AssetList: PropTypes.array,
        conditionIndicesV2PowerTrain: PropTypes.array,
        conditionIndicesV2AssetGroups: PropTypes.array,
        sites: PropTypes.array,
        selectedAssetDetails: PropTypes.object,
        conditionIndicesView: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        hierarchyConditionIndicesLoadingStatus: PropTypes.string,
        hierarchySearchBoxFilter: PropTypes.string,
        hierarchySensorIdentifierFilter: PropTypes.string,
        hierarchySubscriptionTypeFilter: PropTypes.array,
        hierarchyConditionFilter: PropTypes.array,
        hierarchyAssetTypeFilter: PropTypes.string,
        hierarchyLastUpdatedTimestamp: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.instanceOf(Date)]),
        hierarchyConditionIndicesData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
        assetsLastUpdatedTimestamp: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.instanceOf(Date)]),
        listConditionIndicesData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    }),
    actions: PropTypes.shape({
        setAssetTypeFilter: PropTypes.func,
        toggleConditionFilter: PropTypes.func,
        setSubscriptionTypeFilter: PropTypes.func,
        resetFilters: PropTypes.func,
        setSearchboxFilter: PropTypes.func,
        setSensorIdentifierFilter: PropTypes.func,
        setActiveTab: PropTypes.func,
        getSubscriptionTypes: PropTypes.func,
        setNavigationSearch: PropTypes.func,
        setConditionIndicesView: PropTypes.func,
        getHierarchyConditionIndices: PropTypes.func,
        setSelectedAssetAction: PropTypes.func,
        setSelectedNotification: PropTypes.func,
        setActiveTabV2: PropTypes.func,
    })
};

export default ConditionIndicesOverviewComponent;
